/**
 * Билдеры deeplinks для мессенджеров (Экран 2.4.4 Магазины).
 * Pure-функции: input → URL string. Валидация на входе, throw при невалидных данных.
 *
 * WhatsApp: https://wa.me/<E164-без-плюса>?text=<urlencoded>
 * Telegram: https://t.me/<username-без-@>?text=<urlencoded>
 * MAX:      см. {@link buildMaxLink} — placeholder, BACKLOG B-010.
 *
 * Платформо-зависимые MAX-ссылки (Фаза 6.3, ADR-0064 амендмент): `buildMaxOpenLink`
 * и `buildMaxShareLink` принимают `platform` (по умолчанию — авто-детект по UA),
 * остаются чистыми при явной передаче платформы — это позволяет юнит-тестам
 * покрыть все ветки без подмены navigator.
 */
import { isAndroid, isIOS } from './platform';

/**
 * B-010 ЗАКРЫТ (Фаза 6.1) официальными фактами MAX (dev.max.ru/help/deeplinks):
 * - шеринг текста: `https://max.ru/:share?text=<URL-enc>` (моб. iOS 2.7+/Android 2.9+);
 * - чат ПО НОМЕРУ ТЕЛЕФОНА в MAX НЕВОЗМОЖЕН (приватность) — аналога wa.me нет;
 * - чат по профилю: `max.ru/u/<хеш>` или username.
 * Флаг оставлен `false` как маркер «формат известен/не placeholder».
 */
export const MAX_LINK_PLACEHOLDER_TODO = false;

/** WhatsApp deeplink. Phone — E.164 (с/без +); пробелы (включая NBSP через
 * `\s`), скобки, ASCII-дефис и Unicode dashes (figure-dash, en-dash, em-dash,
 * горизонтальные тире, minus sign) игнорируются. Реальные партнёрские
 * телефоны часто содержат Unicode разделители при copy-paste из контактов.
 * Буквы НЕ стрипаются — телефон с буквами throw-ит invalid.
 */
export function buildWhatsAppLink(phone: string, message: string): string {
  const cleaned = phone.replace(/[\s()+\-‐-―−]/g, '');
  if (!/^\d{7,15}$/.test(cleaned)) {
    throw new Error(`Invalid WhatsApp phone: ${phone}`);
  }
  return `https://wa.me/${cleaned}?text=${encodeURIComponent(message)}`;
}

/** Telegram deeplink по username (канал/бот/аккаунт). Username — без @, латиница/цифры/`_`. */
export function buildTelegramLink(username: string, message: string): string {
  const cleaned = username.replace(/^@/, '');
  if (!/^[A-Za-z0-9_]{3,32}$/.test(cleaned)) {
    throw new Error(`Invalid Telegram username: ${username}`);
  }
  return `https://t.me/${cleaned}?text=${encodeURIComponent(message)}`;
}

/**
 * Telegram deeplink по НОМЕРУ телефона (Фаза 6.1, item 1): `https://t.me/+<цифры>`.
 * Открывает чат с контактом, если номер зарегистрирован в Telegram (иначе Telegram
 * сам предупредит). Prefill-текст для phone-ссылки Telegram не поддерживает, поэтому
 * сообщение не передаём. Телефонные разделители/«+» чистятся как в buildWhatsAppLink.
 */
export function buildTelegramPhoneLink(phone: string): string {
  const cleaned = phone.replace(/[\s()+\-‐-―−]/g, '');
  if (!/^\d{7,15}$/.test(cleaned)) {
    throw new Error(`Invalid Telegram phone: ${phone}`);
  }
  return `https://t.me/+${cleaned}`;
}

/** `tel:`-ссылка для городских/любых номеров (Фаза 6.1, item 1 — «Позвонить»). */
export function buildCallLink(phone: string): string {
  const cleaned = phone.replace(/[\s()\-‐-―−]/g, '');
  if (!/^\+?\d{5,15}$/.test(cleaned)) {
    throw new Error(`Invalid phone for tel: ${phone}`);
  }
  return `tel:${cleaned.startsWith('+') ? cleaned : '+' + cleaned}`;
}

/**
 * MAX deeplink ЧАТА (Фаза 6.1, item 1/3): ТОЛЬКО по username или прямой
 * `max.ru`-ссылке (профиль). Чат по номеру телефона в MAX невозможен (факт
 * dev.max.ru) — поэтому телефонный `max_id` (`+7…`) ОТКЛОНЯЕТСЯ (throw → кнопка
 * MAX не рисуется). Откатывает Phase-6 «телефон-толерантный MAX-чат».
 */
export function buildMaxLink(id: string, message: string): string {
  const trimmed = id.trim();
  // Прямая max.ru-ссылка (профиль/чат) — используем verbatim.
  if (/^https:\/\/max\.ru\//i.test(trimmed)) return trimmed;
  const cleaned = trimmed.replace(/^@/, '');
  // Username: латиница/цифры/_, ОБЯЗАТЕЛЬНО с буквой (отсекает телефон/чистые цифры).
  if (/^[A-Za-z0-9_]{2,32}$/.test(cleaned) && /[A-Za-z_]/.test(cleaned)) {
    return `https://max.ru/${cleaned}?text=${encodeURIComponent(message)}`;
  }
  throw new Error(`Invalid MAX id (chat by phone/number unsupported): ${id}`);
}

/**
 * Платформа для выбора схемы MAX-ссылки (Фаза 6.3, ADR-0064 амендмент).
 * `https://max.ru` из браузера НЕ открывает приложение MAX (подтверждено живым
 * тестом — грузится промо-сайт «Скачать приложение»). Cowork-разбор
 * AndroidManifest (`ru.oneme.app`, Хабр/939868): есть `LinkInterceptorActivity`
 * с App Links на `max.ru` (autoVerify), но из браузера переход не срабатывает.
 * Стандартное решение — `intent://`-URL для Android Chrome.
 */
export type MaxPlatform = 'android' | 'ios' | 'desktop';

/**
 * Стратегия открытия приложения MAX на Android. На устройстве подтверждено:
 *  - `intent://` через `window.open` (Фаза 6.3) НЕ срабатывает;
 *  - `intent://` через `location.href` (Фаза 6.4) ТОЖЕ не перехватывается
 *    приложением — срабатывает `browser_fallback_url`, грузится сайт.
 * Поэтому с Фазы 6.5 активна стратегия `'scheme'` — кастомная схема `max://`
 * напрямую через `location.href`, открытие ТОЛЬКО по явной кнопке (user-gesture).
 *  - `'scheme'` (дефолт 6.5): `max://max.ru<path>` — точный хост/путь схемы НЕ
 *    задокументированы (пакет `ru.oneme.app`, домен one.me); хост `max.ru` взят
 *    по аналогии с App Links манифеста. ЭКСПЕРИМЕНТАЛЬНО, проверяется на устройстве.
 *  - `'intent'` (откат): `intent://…;package=ru.oneme.app;…` с https-фолбэком —
 *    оставлен за константой для быстрого отката без новой фазы.
 */
export type MaxOpenStrategy = 'intent' | 'scheme';
export const MAX_OPEN_STRATEGY: MaxOpenStrategy = 'scheme';

const MAX_HTTPS_BASE = 'https://max.ru';
const MAX_ANDROID_PACKAGE = 'ru.oneme.app';

/** Авто-детект платформы по UA (переиспользует {@link isAndroid}/{@link isIOS}). */
export function detectMaxPlatform(): MaxPlatform {
  if (isAndroid()) return 'android';
  if (isIOS()) return 'ios';
  return 'desktop';
}

/**
 * Android Chrome intent://-URL с https-фолбэком на тот же путь (`S.browser_fallback_url`).
 * Если приложение `ru.oneme.app` установлено — открывается оно; иначе Chrome идёт
 * на https-страницу. ЭКСПЕРИМЕНТАЛЬНО: формат проверяется только на устройстве.
 */
function maxAndroidIntent(path: string): string {
  const httpsUrl = `${MAX_HTTPS_BASE}${path}`;
  return (
    `intent://max.ru${path}#Intent;scheme=https;package=${MAX_ANDROID_PACKAGE};` +
    `S.browser_fallback_url=${encodeURIComponent(httpsUrl)};end`
  );
}

/**
 * Кастомная схема `max://` (активная стратегия Фазы 6.5). Хост `max.ru` — по
 * аналогии с App Links манифеста (точный хост/путь схемы НЕ задокументированы,
 * это догадка, уточняется на устройстве). Открывается ТОЛЬКО прямой навигацией
 * (location.href) по явной кнопке; фолбэка на https нет (если приложение не
 * установлено — навигация просто не приведёт никуда).
 *  - открыть приложение (path='') → `max://max.ru`;
 *  - share (path='/:share?text=…') → `max://max.ru/:share?text=…`.
 */
function maxAndroidScheme(path: string): string {
  return `max://max.ru${path}`;
}

/** Android-URL для приложения MAX по выбранной стратегии (intent|scheme). */
function maxAndroidUrl(path: string, strategy: MaxOpenStrategy): string {
  return strategy === 'scheme' ? maxAndroidScheme(path) : maxAndroidIntent(path);
}

/**
 * MAX «открыть приложение» по номеру (Фаза 6.2, item 2; платформо-ветка — Фаза 6.3/6.4).
 * Чата по номеру телефона в MAX НЕ существует (факт ADR-0064 §1, dev.max.ru),
 * поэтому deeplink на конкретный чат построить нельзя. UX-обход: клиент копирует
 * НОМЕР в буфер (см. ShopsListView), а эта функция открывает САМО приложение MAX
 * (путь = корень), куда номер вставляется в поиск контакта.
 * - Android: `intent://` (или `max://` по стратегии) — открывать прямой навигацией;
 * - iOS/Desktop: `https://max.ru` (кастомная схема `max://` для iOS неизвестна —
 *   универсальные ссылки Safari могут сработать из https; уточнить на устройстве).
 * Никогда не throw-ит (в отличие от канального `buildMaxLink`).
 */
export function buildMaxOpenLink(
  platform: MaxPlatform = detectMaxPlatform(),
  strategy: MaxOpenStrategy = MAX_OPEN_STRATEGY,
): string {
  if (platform === 'android') return maxAndroidUrl('', strategy);
  return MAX_HTTPS_BASE;
}

/**
 * Билдеры «переслать клиенту» (Фаза 6, item 4/6/7) — share БЕЗ конкретного
 * получателя (открывают выбор контакта с готовым текстом). Отличаются от
 * dealer-request билдеров выше (там phone/username получателя).
 */

/** WhatsApp share: весь текст (с URL) в `?text=`. */
export function buildWhatsAppShareLink(text: string): string {
  return `https://wa.me/?text=${encodeURIComponent(text)}`;
}

/**
 * Telegram share (B-222): весь текст вместе со ссылкой кладём в параметр `url`
 * (`t.me/share/url?url=<сообщение+ссылка>`). Подтверждено на проде (скрин Aibek):
 * Telegram показывает сообщение, затем ссылку инлайн ОДИН раз и открывает выбор
 * контакта. Вариант `url=<ссылка>&text=<сообщение>` был откатан — на устройстве он
 * хуже. ВАЖНО: дубль ссылки в рефералке был не из-за формы, а из-за разделителя
 * `\n` между сообщением и ссылкой; ForwardSheet склеивает их через ПРОБЕЛ → дубля
 * нет. WhatsApp/MAX share получают тот же текст в своих `?text=`.
 */
export function buildTelegramShareLink(fullText: string): string {
  return `https://t.me/share/url?url=${encodeURIComponent(fullText)}`;
}

/**
 * MAX share (Фаза 6.1, item 2): официальный формат `https://max.ru/:share?text=`
 * (dev.max.ru/help/deeplinks — открывает «Отправить в MAX» с готовым текстом).
 * Платформо-ветка (Фаза 6.3): на Android оборачиваем тот же путь в `intent://`
 * (пакет `ru.oneme.app`, https-фолбэк), иначе из браузера открывается промо-сайт,
 * а не приложение. iOS/Desktop — https как раньше. ЭКСПЕРИМЕНТАЛЬНО (on-device).
 */
export function buildMaxShareLink(
  text: string,
  platform: MaxPlatform = detectMaxPlatform(),
  strategy: MaxOpenStrategy = MAX_OPEN_STRATEGY,
): string {
  const path = `/:share?text=${encodeURIComponent(text)}`;
  if (platform === 'android') return maxAndroidUrl(path, strategy);
  return `${MAX_HTTPS_BASE}${path}`;
}
